// Ring 1 — sole owner of share-card composition + clipboard + Twitter intent.
//
// Read-only consumer of RunState (D-LC-02). NEVER mutates RunState.pocket
// or RunState.cashier — this file consumes the cashier as a value
// parameter, never dereferences RunState directly. PR-review invariant.
//
// PITFALLS guarded (per RESEARCH §Common Pitfalls):
//   #1  Canvas-taint: composition uses ONLY Phaser primitives
//       (Text, Rectangle, RenderTexture). NO image textures = NO
//       cross-origin asset path = NO toBlob SecurityError. Audience-fatal.
//   #3  clipboard.write user-gesture: caller (RunOverScene) reaches
//       copyImageToClipboard SYNCHRONOUSLY from the click handler. The
//       blob is composed once at scene-create so by click time it's in memory.
//   #4  URL.createObjectURL leak: downloadFallback always schedules
//       URL.revokeObjectURL via setTimeout(0). Phase-5 STATE.md gate
//       "5 consecutive runs without refresh; heap plateaus" gates this.
//   #5  Popup-blocker: openTwitterIntent uses `window.open(...,'_blank',
//       'noopener,noreferrer')`. Caller MUST invoke from inside a click
//       handler with NO `await` between user gesture and window.open.
//   #10 Snapshot-before-render race: compose() defers the snapshot by
//       one frame via `scene.time.delayedCall(0, ...)` so all draw uploads
//       complete before the texture readback.
//
// Twitter URL: 'https://twitter.com/intent/tweet' is canonical. The
// rebrand-domain intent path is a known regression — it routes to the
// in-app login screen on mobile and breaks the share flow. Hard-code
// the canonical URL only; CI grep guards against the regressed form.

import Phaser from 'phaser';
import type { CashierResult } from '../run/RunState';
import { isPremiumHand, encodeSeedShort } from '../run/shareSummary';
import { CARD_GLYPH_SHARE, SUIT_COLOR } from '../ui/CardGlyph';

// Share-card layout constants (UI-SPEC §"Visual Layout — Share Card (1200×630)").
// Visual constants co-located per CashierScene precedent (tunables.ts is for
// platformer-feel only). NOT a CARD_GLYPH internal — these govern the layout
// around the card row, not the card primitive itself.
const SHARE_W = 1200;
const SHARE_H = 630;
const BG_HEX = 0x08050a;
const BRAND_Y = 64;
const BRAND_LEFT_X = 48;
const BRAND_RIGHT_X = SHARE_W - 48;
const BRAND_PX = 32;
const BANNER_Y = 200;
const BANNER_PX = 120;
const CARDS_Y = 350;            // top of the 5-card row
const CARDS_GAP = 16;           // UI-SPEC sm token at share-card scale
const SCORE_Y = 560;
const SCORE_PX = 96;
const FOOTER_Y = 600;
const FOOTER_PX = 24;
// Color tokens (UI-SPEC §Color):
const COLOR_PRIMARY = '#f5e1c8';   // secondary cream — body text
const COLOR_PREMIUM = '#f5c542';   // accent gold — premium hand banner + score
const COLOR_FOOTER = '#888888';    // hint dim — degenkong.fun line
// Twitter intent URL prefix (RESEARCH Pattern 5):
const TWITTER_INTENT_BASE = 'https://twitter.com/intent/tweet?text=';

export interface ShareCardOutput {
  blob: Blob;
  dataURL: string;   // for in-viewport preview via Phaser.Textures.addBase64
}

export const ShareCardSystem = {
  /**
   * Compose the 1200×630 share card off-screen, encode to PNG Blob.
   * Cached once per RunOverScene.create() per D-RT-04.
   */
  async compose(
    scene: Phaser.Scene,
    cashier: CashierResult,
    seed: number,
  ): Promise<ShareCardOutput> {
    // (1) Off-screen RenderTexture. (-2000,-2000) puts it well outside
    // the 320×240 viewport; setVisible(false) is a second guard.
    const rt = scene.add.renderTexture(-2000, -2000, SHARE_W, SHARE_H);
    rt.setVisible(false);
    rt.fill(BG_HEX);

    // (2) Build all draw sources via scene.make.* (second arg `false` —
    // suppresses display-list auto-add so they don't flash in the
    // 320×240 viewport between create and snapshot).
    const seedShort = encodeSeedShort(seed);
    const bannerColor = isPremiumHand(cashier.handName) ? COLOR_PREMIUM : COLOR_PRIMARY;

    const brandText = scene.make.text({
      x: BRAND_LEFT_X, y: BRAND_Y,
      text: '🃏 degen-kong',
      style: { fontFamily: 'monospace', fontSize: `${BRAND_PX}px`, color: COLOR_PRIMARY },
    }, false);
    brandText.setOrigin(0, 0.5);

    const seedText = scene.make.text({
      x: BRAND_RIGHT_X, y: BRAND_Y,
      text: `seed: ${seedShort}`,
      style: { fontFamily: 'monospace', fontSize: `${BRAND_PX}px`, color: COLOR_PRIMARY },
    }, false);
    seedText.setOrigin(1, 0.5);

    const handBanner = scene.make.text({
      x: SHARE_W / 2, y: BANNER_Y,
      text: cashier.handName.toUpperCase(),
      style: { fontFamily: 'monospace', fontSize: `${BANNER_PX}px`, color: bannerColor },
    }, false);
    handBanner.setOrigin(0.5, 0);

    // 5-card row (UI-SPEC math: 5 × 120 + 4 × 16 = 664 px wide; centered at start x=268).
    // CHOSEN PATH (WARNING 6 resolution): construct GameObjects directly via
    // `new Phaser.GameObjects.Rectangle(...)` and `scene.make.text(..., false)`,
    // both of which skip the display list entirely. Rectangle has no factory
    // method on `GameObjectCreator` (`scene.make.rectangle` does not exist —
    // see Phaser type defs for `class GameObjectCreator`), so the direct
    // constructor is the only zero-flicker path — even on slow GPUs where
    // setVisible toggles can render one frame before the next vsync.
    // CashierScene continues to use `makeCardGlyph()` (which calls scene.add.*)
    // for the 24×36 viewport scale where display-list addition IS correct.
    const totalW = 5 * CARD_GLYPH_SHARE.cardW + 4 * CARDS_GAP;
    const startX = (SHARE_W - totalW) / 2;
    const cardObjects: Phaser.GameObjects.GameObject[] = [];
    for (let i = 0; i < cashier.locked5.length; i++) {
      const card = cashier.locked5[i];
      const x = startX + i * (CARD_GLYPH_SHARE.cardW + CARDS_GAP) + CARD_GLYPH_SHARE.cardW / 2;
      const y = CARDS_Y + CARD_GLYPH_SHARE.cardH / 2;   // CARDS_Y is top edge; rectangle origin is center
      const bg = new Phaser.GameObjects.Rectangle(
        scene, x, y, CARD_GLYPH_SHARE.cardW, CARD_GLYPH_SHARE.cardH, SUIT_COLOR[card.suit], 1,
      );
      bg.setStrokeStyle(CARD_GLYPH_SHARE.strokePx, 0x08050a);
      const label = scene.make.text({
        x, y,
        text: `${card.rank}${card.suit}`,
        style: { fontFamily: 'monospace', fontSize: `${CARD_GLYPH_SHARE.textPx}px`, color: '#08050a' },
      }, false);
      label.setOrigin(0.5);
      cardObjects.push(bg, label);
    }

    const scoreText = scene.make.text({
      x: SHARE_W / 2, y: SCORE_Y,
      text: `${cashier.score.toLocaleString('en-US')} chips`,
      style: { fontFamily: 'monospace', fontSize: `${SCORE_PX}px`, color: COLOR_PREMIUM },
    }, false);
    scoreText.setOrigin(0.5, 0);

    const footerText = scene.make.text({
      x: SHARE_W / 2, y: FOOTER_Y,
      text: 'degenkong.fun',
      style: { fontFamily: 'monospace', fontSize: `${FOOTER_PX}px`, color: COLOR_FOOTER },
    }, false);
    footerText.setOrigin(0.5, 0);

    // (3) Draw all sources into the RT. With `make.*` factories the card
    // objects were never added to the display list, so no setVisible toggle
    // is required — RT.draw() reads the geometry directly.
    const drawList: Phaser.GameObjects.GameObject[] = [
      brandText, seedText, handBanner, ...cardObjects, scoreText, footerText,
    ];
    rt.draw(drawList);

    // (4) Defer snapshot one frame to flush WebGL texture uploads (PITFALL #10).
    await new Promise<void>((resolve) => scene.time.delayedCall(0, resolve));

    // (5) Snapshot. Wrap callback API in a Promise. Narrow the union type
    // (Phaser snapshotCallback declares HTMLImageElement | Phaser.Display.Color
    // — the Color form only fires for snapshotPixel).
    const image = await new Promise<HTMLImageElement>((resolve, reject) => {
      rt.snapshotArea(0, 0, SHARE_W, SHARE_H, (img) => {
        if (img instanceof HTMLImageElement) resolve(img);
        else reject(new Error('Unexpected snapshot type (not HTMLImageElement)'));
      }, 'image/png');
    });

    // (6) Decode HTMLImageElement.src (data URL) → Blob via fetch (RESEARCH Pattern 2).
    const blob = await fetch(image.src).then((r) => r.blob());

    // (7) Cleanup — destroy all draw sources and the RT. The RT itself
    // owns no persistent texture cache key (we never called saveTexture).
    for (const obj of drawList) obj.destroy();
    rt.destroy();

    return { blob, dataURL: image.src };
  },

  /**
   * D-IM-01 step 1: copy share-card PNG to clipboard.
   * Returns true on success, false on rejection (caller routes to downloadFallback).
   *
   * IMPORTANT: caller MUST invoke this synchronously from inside a click/keypress
   * handler. Awaiting between gesture and `clipboard.write` consumes the
   * transient activation in Safari/Firefox (PITFALL #3).
   */
  async copyImageToClipboard(blob: Blob): Promise<boolean> {
    if (typeof navigator === 'undefined' || !navigator.clipboard || !('write' in navigator.clipboard)) {
      return false;
    }
    try {
      const item = new ClipboardItem({ 'image/png': blob });
      await navigator.clipboard.write([item]);
      return true;
    } catch {
      return false;
    }
  },

  /** D-IM-05: text-only clipboard copy for the [C] button. */
  async copyTextToClipboard(text: string): Promise<boolean> {
    if (typeof navigator === 'undefined' || !navigator.clipboard || !('writeText' in navigator.clipboard)) {
      return false;
    }
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      return false;
    }
  },

  /**
   * D-IM-01 step 2: open Twitter compose with the emoji-string pre-filled.
   * Hard-coded to twitter.com/intent/tweet — the rebrand-domain form is a
   * known regression to the in-app login screen and is forbidden by CI grep.
   *
   * MUST be called from inside a click handler — popup blockers intercept
   * window.open calls that don't trace back to a user gesture (PITFALL #5).
   */
  openTwitterIntent(emojiSummary: string): void {
    const intentUrl = `${TWITTER_INTENT_BASE}${encodeURIComponent(emojiSummary)}`;
    window.open(intentUrl, '_blank', 'noopener,noreferrer');
  },

  /**
   * D-IM-02: download fallback when clipboard.write rejects.
   * Triggers a synthetic <a download> click. Always revokes the object URL
   * via setTimeout(0) (PITFALL #4 — memory leak prevention).
   */
  downloadFallback(blob: Blob, filename = 'degen-kong-run.png'): void {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    // setTimeout 0 — some browsers need the URL to remain valid for one tick after click.
    setTimeout(() => URL.revokeObjectURL(url), 0);
  },
};
